import clsx from 'clsx';
import { memo } from 'react';

import type { SharedIconProps } from '../../types/SharedIconProps';

import { useNumberFormat } from '../../context/NumberFormat/NumberFormat';
import { Icon } from '../Icon/Icon';
import { Text } from '../Text/Text';
import { resolveCountBadgeCountText } from './utils';
import './CountBadge.css';

export type CountBadgeProps = {
  /** Raw numeric count used for render gating and overflow threshold logic. */
  count: number;
  /** Optional leading icon. */
  icon?: SharedIconProps;
  /**
   * Maximum count before the value is treated as overflow. Uncapped by
   * default — the full count renders.
   */
  maxCount?: number;
};

export const CountBadge = memo(({ count, icon, maxCount }: CountBadgeProps) => {
  const formatNumber = useNumberFormat();

  if (count <= 0) {
    return null;
  }

  const isOverflow = maxCount !== undefined && count > maxCount;
  const countText = resolveCountBadgeCountText(count, formatNumber, maxCount);

  return (
    <div
      className={clsx(
        'eds-count-badge',
        icon && 'eds-count-badge--has-icon',
        isOverflow && 'eds-count-badge--overflow',
      )}
    >
      {icon && (
        <Icon
          filled={icon.filled}
          icon={icon.key}
          label={icon.label}
          size="xs"
        />
      )}
      <Text truncate={1} variant="body-semi-s">
        {countText}
      </Text>
    </div>
  );
});

CountBadge.displayName = 'CountBadge';
